import Axios from 'axios';
import React, { useEffect, useState, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  message,
  DatePicker,
  Input,
  Space,
  Upload,
  Tooltip,
  Switch,
} from 'antd';
import {
  UploadOutlined,
  CopyOutlined,
  EditOutlined,
  DeleteOutlined,
  LockOutlined,
  UnlockOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
// テーブル強化 PJ Ph.3: 列リサイズ + ソート + 省略
import {
  PageContainer,
  StatusBadge,
  useResizableColumns,
  ellipsisColumn,
  textSorter,
  dateSorter,
  numberSorter,
} from '../../common';

const { confirm } = Modal;
const { Dragger } = Upload;

const PublicDocMgrPage = (props) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [expiresAt, setExpiresAt] = useState(null);
  const [password, setPassword] = useState('');

  // 編集用Modal
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [editExpiresAt, setEditExpiresAt] = useState(null);

  // パスワード用Modal
  const [pwModalVisible, setPwModalVisible] = useState(false);
  const [pwDoc, setPwDoc] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const usr = useSelector((state) => state.user);

  // 一覧取得
  const fetchDocs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await Axios.get('/api/publicDoc');
      if (res.data.success) {
        setDocs(res.data.docs);
      }
    } catch (error) {
      console.error(error);
      message.error('データ取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (usr?.userData?.isAuth) {
      fetchDocs();
    }
  }, [usr, fetchDocs]);

  // ファイルアップロード
  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning('ファイルを選択してください');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', fileList[0]);
      if (expiresAt) {
        formData.append('expires_at', expiresAt.endOf('day').toISOString());
      }
      if (password) {
        formData.append('password', password);
      }

      const res = await Axios.post('/api/publicDoc/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.data.success) {
        message.success('アップロード完了');
        setFileList([]);
        setExpiresAt(null);
        setPassword('');
        fetchDocs();
      } else {
        message.error(res.data.error || 'アップロードに失敗しました');
      }
    } catch (error) {
      console.error(error);
      message.error('アップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  // URL複写
  const copyUrl = (shortId) => {
    const url = `${window.location.origin}/d/${shortId}`;
    navigator.clipboard.writeText(url).then(() => {
      message.success('URLをクリップボードにコピーしました');
    });
  };

  // 削除確認
  const showDeleteConfirm = (doc) => {
    confirm({
      title: 'ファイルを削除しますか？',
      content: `「${doc.original_filename}」を削除します。S3上のファイルも削除されます。`,
      okText: '削除',
      okType: 'danger',
      cancelText: 'キャンセル',
      async onOk() {
        try {
          const res = await Axios.delete(`/api/publicDoc/${doc._id}`);
          if (res.data.success) {
            message.success('削除完了');
            fetchDocs();
          }
        } catch (error) {
          message.error('削除に失敗しました');
        }
      },
    });
  };

  // 編集Modal
  const openEditModal = (doc) => {
    setEditDoc(doc);
    setEditExpiresAt(doc.expires_at ? dayjs(doc.expires_at) : null);
    setEditModalVisible(true);
  };

  const handleEditSave = async () => {
    try {
      const res = await Axios.patch(`/api/publicDoc/${editDoc._id}`, {
        expires_at: editExpiresAt ? editExpiresAt.endOf('day').toISOString() : null,
      });
      if (res.data.success) {
        message.success('更新完了');
        setEditModalVisible(false);
        fetchDocs();
      }
    } catch (error) {
      message.error('更新に失敗しました');
    }
  };

  // パスワードModal
  const openPwModal = (doc) => {
    setPwDoc(doc);
    setNewPassword('');
    setPwModalVisible(true);
  };

  const handlePwSave = async () => {
    try {
      const res = await Axios.patch(`/api/publicDoc/${pwDoc._id}/password`, {
        password: newPassword || null,
      });
      if (res.data.success) {
        message.success(
          newPassword ? 'パスワードを設定しました' : 'パスワードを削除しました'
        );
        setPwModalVisible(false);
        fetchDocs();
      }
    } catch (error) {
      message.error('パスワード更新に失敗しました');
    }
  };

  // 有効/無効切替
  const toggleActive = async (doc) => {
    try {
      const res = await Axios.patch(`/api/publicDoc/${doc._id}`, {
        is_active: !doc.is_active,
      });
      if (res.data.success) {
        message.success(doc.is_active ? '無効化しました' : '有効化しました');
        fetchDocs();
      }
    } catch (error) {
      message.error('更新に失敗しました');
    }
  };

  // 日時フォーマット
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${d.getFullYear()}/${('0' + (d.getMonth() + 1)).slice(-2)}/${('0' + d.getDate()).slice(-2)}`;
  };

  // ファイルサイズフォーマット
  const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // 期限状態の判定
  const getExpiryTag = (expiresAt) => {
    if (!expiresAt) return <StatusBadge tone="info">無期限</StatusBadge>;
    const now = new Date();
    const exp = new Date(expiresAt);
    if (exp < now) return <StatusBadge tone="error">期限切れ</StatusBadge>;
    const daysLeft = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 7)
      return <StatusBadge tone="warning">残{daysLeft}日</StatusBadge>;
    return (
      <StatusBadge tone="success">{formatDate(expiresAt)}まで</StatusBadge>
    );
  };

  // Dragger設定（手動アップロード）
  const draggerProps = {
    onRemove: () => {
      setFileList([]);
    },
    beforeUpload: (file) => {
      setFileList([file]);
      return false; // 自動アップロードを無効化
    },
    fileList,
    maxCount: 1,
  };

  // テーブル強化 PJ Ph.3: <Column> 子要素 → columns 配列。元から width/scroll を持つ。
  // ファイル名は省略 + ツールチップ、各列にソート、操作列は既存ボタンを維持。
  const rawColumns = [
    ellipsisColumn({
      title: 'ファイル名',
      dataIndex: 'original_filename',
      key: 'original_filename',
      width: 250,
      sorter: textSorter('original_filename'),
    }),
    {
      title: '公開URL',
      dataIndex: 'short_id',
      key: 'short_id',
      width: 140,
      sorter: textSorter('short_id'),
      render: (shortId) => (
        <Space>
          <code>/d/{shortId}</code>
          <Tooltip title="URLをコピー">
            <Button
              type="link"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyUrl(shortId)}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: '閲覧期限',
      dataIndex: 'expires_at',
      key: 'expires_at',
      width: 140,
      sorter: dateSorter('expires_at'),
      render: (val) => getExpiryTag(val),
    },
    {
      title: 'PW',
      dataIndex: 'has_password',
      key: 'has_password',
      width: 60,
      align: 'center',
      sorter: (a, b) => (a.has_password ? 1 : 0) - (b.has_password ? 1 : 0),
      render: (hasPw) =>
        hasPw ? (
          <LockOutlined style={{ color: '#faad14' }} />
        ) : (
          <UnlockOutlined style={{ color: 'var(--grey-30)' }} />
        ),
    },
    {
      title: 'DL数',
      dataIndex: 'download_count',
      key: 'download_count',
      width: 70,
      align: 'right',
      sorter: numberSorter('download_count'),
    },
    {
      title: 'サイズ',
      dataIndex: 'file_size',
      key: 'file_size',
      width: 90,
      sorter: numberSorter('file_size'),
      render: (val) => formatSize(val),
    },
    {
      title: '有効',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 70,
      align: 'center',
      sorter: (a, b) => (a.is_active ? 1 : 0) - (b.is_active ? 1 : 0),
      render: (val, record) => (
        <Switch
          size="small"
          checked={val}
          onChange={() => toggleActive(record)}
        />
      ),
    },
    {
      title: '登録日',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 100,
      sorter: dateSorter('createdAt'),
      render: (val) => formatDate(val),
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="閲覧期限を編集">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Tooltip title="パスワード設定">
            <Button
              size="small"
              icon={<LockOutlined />}
              onClick={() => openPwModal(record)}
            />
          </Tooltip>
          <Tooltip title="削除">
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => showDeleteConfirm(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const { columns, components, scroll, wrapRef } = useResizableColumns(rawColumns);

  return (
    <>
      <PageContainer fluid>
      <h2>外部ファイル共有管理</h2>

      {/* アップロードエリア */}
      <div
        style={{
          background: 'var(--grey-10)',
          border: '1px solid var(--grey-30)',
          borderRadius: 8,
          padding: 24,
          marginBottom: 24,
        }}
      >
        <h3 style={{ marginBottom: 16 }}>ファイルアップロード</h3>
        <Dragger {...draggerProps} style={{ marginBottom: 16 }}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            ファイルをドラッグ＆ドロップ、またはクリックして選択
          </p>
        </Dragger>

        <Space
          size="middle"
          style={{ marginTop: 16, flexWrap: 'wrap' }}
          align="end"
        >
          <div>
            <div style={{ marginBottom: 4, fontSize: 13 }}>閲覧期限</div>
            <DatePicker
              value={expiresAt}
              onChange={setExpiresAt}
              placeholder="期限なし"
              style={{ width: 180 }}
              allowClear
            />
          </div>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13 }}>
              閲覧パスワード（任意）
            </div>
            <Input.Password
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="未設定"
              style={{ width: 200 }}
              allowClear
            />
          </div>
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={handleUpload}
            loading={uploading}
            disabled={fileList.length === 0}
          >
            アップロード
          </Button>
        </Space>
      </div>

      {/* 一覧テーブル */}
      <div ref={wrapRef}>
        <Table
          columns={columns}
          components={components}
          scroll={scroll}
          dataSource={docs}
          size="middle"
          rowKey="_id"
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: true }}
        />
      </div>
      </PageContainer>

      {/* 閲覧期限編集Modal */}
      <Modal
        title="閲覧期限の編集"
        open={editModalVisible}
        onOk={handleEditSave}
        onCancel={() => setEditModalVisible(false)}
        okText="保存"
        cancelText="キャンセル"
      >
        {editDoc && (
          <div>
            <p style={{ marginBottom: 12 }}>
              ファイル: {editDoc.original_filename}
            </p>
            <div style={{ marginBottom: 4 }}>閲覧期限</div>
            <DatePicker
              value={editExpiresAt}
              onChange={setEditExpiresAt}
              placeholder="期限なし"
              style={{ width: '100%' }}
              allowClear
            />
          </div>
        )}
      </Modal>

      {/* パスワード設定Modal */}
      <Modal
        title="パスワード設定"
        open={pwModalVisible}
        onOk={handlePwSave}
        onCancel={() => setPwModalVisible(false)}
        okText="保存"
        cancelText="キャンセル"
      >
        {pwDoc && (
          <div>
            <p style={{ marginBottom: 12 }}>
              ファイル: {pwDoc.original_filename}
            </p>
            <p style={{ marginBottom: 12, color: 'var(--grey-65)', fontSize: 13 }}>
              {pwDoc.has_password
                ? '現在パスワードが設定されています。新しいパスワードを入力するか、空欄で保存するとパスワードを削除します。'
                : 'パスワードを設定すると、閲覧時にパスワード入力が必要になります。'}
            </p>
            <Input.Password
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={
                pwDoc.has_password
                  ? '新しいパスワード（空欄で削除）'
                  : 'パスワードを入力'
              }
              style={{ width: '100%' }}
              allowClear
            />
          </div>
        )}
      </Modal>
    </>
  );
};

export default PublicDocMgrPage;
